import { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  Alert,
} from "react-native";
import { useRouter, useLocalSearchParams } from "expo-router";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase/client";
import { useThemeColors } from "@/providers/ThemeProvider";

/**
 * Email Verification Screen
 * Users enter the 6-digit token from their email
 */
export default function VerifyScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const email = params.email as string;
  const colors = useThemeColors();
  
  const [token, setToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle token verification
   */
  const handleVerify = async () => {
    if (!token || token.length < 6) {
      Alert.alert("Fout", "Vul de code in");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "signup",
      });

      if (error) {
        throw error;
      }

      Alert.alert("Gelukt!", "Je email is bevestigd. Je wordt nu ingelogd.", [
        {
          text: "OK",
          onPress: () => router.replace("/(tabs)"),
        },
      ]);
    } catch (error: any) {
      Alert.alert(
        "Fout",
        error.message || "De code is ongeldig of verlopen. Probeer opnieuw."
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resend verification email
   */
  const handleResend = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
      });

      if (error) throw error;

      Alert.alert("Verzonden", "Er is een nieuwe code naar je email gestuurd.");
    } catch (error: any) {
      Alert.alert("Fout", error.message || "Kon geen nieuwe email versturen.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        {/* Back Button */}
        <TouchableOpacity
          onPress={() => router.back()}
          className="px-4 py-2"
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>

        <View className="flex-1 px-6 justify-center">
          {/* Icon */}
          <View className="items-center mb-8">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Ionicons name="mail-outline" size={40} color={colors.primary} />
            </View>
            <Text className="text-2xl font-bold text-center" style={{ color: colors.text }}>
              Bevestig je email
            </Text>
            <Text className="mt-2 text-center px-4" style={{ color: colors.textSecondary }}>
              We hebben een code gestuurd naar
            </Text>
            <Text className="font-semibold mt-1" style={{ color: colors.primary }}>
              {email}
            </Text>
          </View>

          {/* Token Input */}
          <View
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            <Text className="mb-2 font-medium text-center" style={{ color: colors.text }}>
              Voer je code in
            </Text>
            <TextInput
              className="rounded-xl py-4 px-6 text-center text-2xl font-bold tracking-widest"
              style={{
                backgroundColor: colors.background,
                borderWidth: 1,
                borderColor: colors.border,
                color: colors.text,
              }}
              placeholder="000000"
              placeholderTextColor={colors.textTertiary}
              value={token}
              onChangeText={setToken}
              keyboardType="number-pad"
              maxLength={6}
              autoFocus
            />
          </View>

          {/* Verify Button */}
          <TouchableOpacity
            className="rounded-xl py-4 items-center mb-4"
            style={{
              backgroundColor: isLoading ? `${colors.primary}80` : colors.primary,
            }}
            onPress={handleVerify}
            disabled={isLoading}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel="Bevestigen"
          >
            <Text className="text-white font-semibold text-lg">
              {isLoading ? "Verifiëren..." : "Bevestig"}
            </Text>
          </TouchableOpacity>

          {/* Resend Link */}
          <View className="items-center">
            <Text className="mb-2" style={{ color: colors.textSecondary }}>
              Geen email ontvangen?
            </Text>
            <TouchableOpacity
              onPress={handleResend}
              disabled={isLoading}
              accessibilityRole="button"
              accessibilityLabel="Verstuur opnieuw"
            >
              <Text className="font-semibold" style={{ color: colors.primary }}>
                Verstuur opnieuw
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
